import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { t } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { Paperclip, X, FileText, Image as ImageIcon, Loader2, Upload } from "lucide-react";

const ALLOWED = ["image/jpeg", "image/png", "image/webp", "image/gif", "application/pdf"];
const MAX_BYTES = 10 * 1024 * 1024;
const MAX_FILES = 5;

const formatSize = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const AttachmentUploader = () => {
  const { language, attachments, setAttachments } = useApp();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pick = () => inputRef.current?.click();

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // reset so same file can be re-picked
    if (attachments.length + files.length > MAX_FILES) {
      toast.error(t(language, "maxFilesReached"));
      return;
    }
    for (const f of files) {
      if (!ALLOWED.includes(f.type)) {
        toast.error(`${f.name}: ${t(language, "fileTypeBad")}`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name}: ${t(language, "fileTooLarge")}`);
        continue;
      }
      setUploading(true);
      try {
        const form = new FormData();
        form.append("file", f);
        const { data } = await api.post("/uploads", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setAttachments((prev) => [...prev, data]);
      } catch (err) {
        toast.error(err?.response?.data?.detail || t(language, "errorGeneric"));
      } finally {
        setUploading(false);
      }
    }
  };

  const remove = (id) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="attachment-uploader">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="text-start">
          <div className="flex items-center gap-2">
            <Paperclip className="h-4 w-4 text-slate-500" />
            <h3 className="text-sm font-semibold text-slate-900">{t(language, "attachEvidence")}</h3>
            <span className="text-[10px] tracking-wide uppercase font-medium text-[#C5A059] bg-[#C5A059]/10 px-2 py-0.5 rounded">
              {t(language, "attachOptional")}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">{t(language, "attachHelper")}</p>
        </div>
        <Button
          data-testid="attach-add-btn"
          type="button"
          variant="outline"
          size="sm"
          onClick={pick}
          disabled={uploading || attachments.length >= MAX_FILES}
          className="flex-shrink-0"
        >
          {uploading ? (
            <><Loader2 className="h-3.5 w-3.5 me-1.5 animate-spin" />{t(language, "uploading")}</>
          ) : (
            <><Upload className="h-3.5 w-3.5 me-1.5" />{t(language, "addFile")}</>
          )}
        </Button>
        <input
          ref={inputRef}
          data-testid="attach-file-input"
          type="file"
          className="hidden"
          multiple
          accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
          onChange={handleFiles}
        />
      </div>

      {attachments.length > 0 && (
        <ul className="space-y-2 mt-4" data-testid="attach-list">
          {attachments.map((a) => {
            const isImage = a.content_type?.startsWith("image/");
            const Icon = isImage ? ImageIcon : FileText;
            return (
              <li
                key={a.id}
                data-testid={`attach-item-${a.id}`}
                className="flex items-center gap-3 border border-slate-200 rounded-lg px-3 py-2 bg-slate-50/50"
              >
                <div className="h-8 w-8 rounded bg-[#006C35]/10 text-[#006C35] flex items-center justify-center flex-shrink-0">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0 text-start">
                  <div className="text-sm text-slate-800 truncate">{a.filename}</div>
                  <div className="text-xs text-slate-500">{formatSize(a.size)}</div>
                </div>
                <button
                  data-testid={`attach-remove-${a.id}`}
                  type="button"
                  onClick={() => remove(a.id)}
                  className="h-7 w-7 rounded flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50"
                  aria-label={t(language, "remove")}
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default AttachmentUploader;
